import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";

  useEffect(() => {
    let alive = true;
    api.get("/categories").then((r) => {
      if (alive) setCats(r.data);
    });
    return () => {
      alive = false;
    };
  }, [setCats]);

  useEffect(() => {
    let alive = true;
    const p = {};
    if (category) p.category = category;
    if (q) p.q = q;
    api.get("/products", { params: p }).then((r) => {
      if (alive) setProducts(r.data);
    });
    return () => {
      alive = false;
    };
  }, [category, q, setProducts]);

  const activeCat = useMemo(() => cats.find((c) => c.slug === category), [cats, category]);
  const title = activeCat ? activeCat.name : "Shop all";
  const description = activeCat ? activeCat.description : "Wireless mics, USB-C mics, adapters, and creator bundles.";

  return (
    <div className="fade-in" data-testid="page-shop">
      <SEO title={title} description={description} path={`/shop${category ? `?category=${category}` : ""}`} />

      <section className="pt-14 md:pt-20 pb-8 max-w-7xl mx-auto px-6 md:px-10">
        <p className="eyebrow mb-3">Shop</p>
        <h1 className="text-h1 text-[#1D1D1F]">{title}</h1>
        <p className="mt-4 text-lg text-[#86868B] max-w-2xl">{description}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid md:grid-cols-[240px_1fr] gap-10">
          <aside className="hidden md:block">
            <p className="eyebrow mb-4">Categories</p>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => setParams({})}
                  className={`text-sm text-left ${!category ? "text-[#0066FF] font-semibold" : "text-[#1D1D1F] hover:text-[#0066FF]"}`}
                  data-testid="filter-all"
                >
                  All products
                </button>
              </li>
              {cats.map((c) => (
                <li key={c.slug}>
                  <button
                    onClick={() => setParams({ category: c.slug })}
                    className={`text-sm text-left ${category === c.slug ? "text-[#0066FF] font-semibold" : "text-[#1D1D1F] hover:text-[#0066FF]"}`}
                    data-testid={`filter-${c.slug}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>

            <p className="eyebrow mb-4 mt-10">Search</p>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products..."
              data-testid="shop-search"
              className="w-full h-11 px-4 rounded-xl bg-[#F5F5F7] border border-transparent focus:border-[#0066FF] focus:bg-white focus:ring-1 focus:ring-[#0066FF] focus:outline-none text-sm"
            />
          </aside>

          <div>
            <div className="md:hidden mb-6">
              <select
                value={category}
                onChange={(e) => setParams(e.target.value ? { category: e.target.value } : {})}
                data-testid="shop-mobile-filter"
                className="w-full h-12 px-4 rounded-xl bg-[#F5F5F7] border-transparent"
              >
                <option value="">All products</option>
                {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>

            {products.length === 0 ? (
              <p className="text-[#86868B] py-20 text-center" data-testid="shop-empty">No products match your filters.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
